// Layer 3 (Transformation): rank-preserving display projection for the
// analytics service's diagnosis envelope. It reads probabilities and evidence;
// it computes no diagnosis.

import type { AnalysisResult } from '@/shared/contracts/analytics/v1';
import {
  caseClockLabel,
  plantRoleLabel,
  sentenceTarpInstruction,
} from '@/versions/v1/modules/advisory/AnalysisRun.transform';
import { oneDecimalPercent } from '@/versions/v1/modules/advisory/AgentInvestigation.transform';

type Diagnosis = NonNullable<AnalysisResult['diagnosis']>;
type Candidate = Diagnosis['candidates'][number];
type DiagnosisEvidence = Candidate['evidence_for'][number];

export type DiagnosisEvidenceView = {
  key: string;
  label: string;
  valueLabel: string;
};

export type AnalysisDiagnosisRow = {
  causeId: string;
  causeLabel: string;
  probability: number;
  probabilityLabel: string;
  evidenceFor: DiagnosisEvidenceView[];
  evidenceAgainst: DiagnosisEvidenceView[];
  controlPlanLabel: string | null;
  tarpLabel: string | null;
  suggested: boolean;
  forReview: boolean;
};

export type AnalysisDiagnosisTarpRoute = {
  key: string;
  label: string;
  causeLabels: string[];
  tier: number;
};

export type AnalysisDiagnosisRoutes = {
  controlPlanLabels: string[];
  tarpRoutes: AnalysisDiagnosisTarpRoute[];
};

function precisionFor(unit: string | null | undefined): number {
  if (unit === 'pH') return 3;
  if (unit === '%') return 2;
  if (unit === 't/h') return 1;
  if (unit === 'points') return 0;
  return 2;
}

function numberLabel(value: number, unit: string | null | undefined): string {
  if (unit === '%') return oneDecimalPercent(value / 100);
  const precision = precisionFor(unit);
  if (precision === 0) return String(Math.round(value));
  return value.toFixed(precision).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function percentLabel(value: number): string {
  return `${oneDecimalPercent(value)}%`;
}

function quantityLabel(value: number, unit: string | null | undefined): string {
  const formatted = numberLabel(value, unit);
  if (unit === 'points') return `${formatted} ${value === 1 ? 'point' : 'points'}`;
  if (unit === '%') return `${formatted}%`;
  return unit ? `${formatted} ${unit}` : formatted;
}

function strongest(evidence: readonly DiagnosisEvidence[]): DiagnosisEvidenceView[] {
  return [...evidence]
    .sort((a, b) => b.weight - a.weight || b.event_time.localeCompare(a.event_time))
    .slice(0, 3)
    .map((item, index) => {
      const observed =
        typeof item.value === 'number'
          ? quantityLabel(item.value, item.unit)
          : item.unit
            ? `${String(item.value)} ${item.unit}`
            : String(item.value);
      const reference =
        item.reference_value === null || item.reference_value === undefined
          ? ''
          : ` vs ${item.reference_label ? `${item.reference_label} ` : ''}${quantityLabel(item.reference_value, item.unit)}`;
      return {
        key: `${item.fact}-${item.rule_id ?? item.signal_role ?? 'fact'}-${index}-${item.event_time}`,
        label: item.label,
        valueLabel: `${observed}${reference} · ${caseClockLabel(item.event_time)}`,
      };
    });
}

function controlPlanLabel(candidate: Candidate): string | null {
  const row = candidate.control_plan;
  if (!row) return null;
  const signalLabel = row.signal_role === 'rougher_feed_ph' ? 'Rougher feed pH' : roleLabel(row.signal_role);
  // A high-only Control Plan row carries a null low spec.
  const bounds = row.spec_low == null
    ? `${numberLabel(row.spec_high, row.unit)} ${row.unit} high`
    : `${numberLabel(row.spec_low, row.unit)}–${numberLabel(row.spec_high, row.unit)} ${row.unit}`;
  return `${signalLabel} · ${bounds}`;
}

function roleLabel(role: string): string {
  return plantRoleLabel(role);
}

function tarpLabel(candidate: Candidate): string | null {
  const tarp = candidate.tarp;
  if (!tarp) return null;
  return `TARP ${tarp.tier} · ${roleLabel(tarp.owner_role)} · ${sentenceTarpInstruction(tarp.instruction)} · ${tarp.escalate_after_minutes}m → ${roleLabel(tarp.escalate_to_role)}`;
}

/** Engine order and probability pass through without re-sorting or rounding. */
export function analysisDiagnosisRows(result: AnalysisResult): AnalysisDiagnosisRow[] {
  const diagnosis = result.diagnosis;
  if (!diagnosis) return [];
  return diagnosis.candidates.map((candidate, index) => {
    const next = diagnosis.candidates[index + 1];
    const previous = diagnosis.candidates[index - 1];
    const comparison = next
      ? `vs next ${percentLabel(next.probability)}`
      : previous
        ? `vs previous ${percentLabel(previous.probability)}`
        : 'sole cause';
    return {
      causeId: candidate.cause_id,
      causeLabel: candidate.cause_label,
      probability: candidate.probability,
      probabilityLabel: `${percentLabel(candidate.probability)} ${comparison}`,
      evidenceFor: strongest(candidate.evidence_for),
      evidenceAgainst: strongest(candidate.evidence_against),
      controlPlanLabel: controlPlanLabel(candidate),
      tarpLabel: tarpLabel(candidate),
      suggested: index === 0 && diagnosis.status === 'suggest',
      forReview:
        index === 0 &&
        diagnosis.status === 'review' &&
        (!next || candidate.probability > next.probability),
    };
  });
}

/** One shared Control Plan read and one TARP read per distinct routed rung. */
export function analysisDiagnosisRoutes(result: AnalysisResult): AnalysisDiagnosisRoutes {
  const diagnosis = result.diagnosis;
  if (!diagnosis) return { controlPlanLabels: [], tarpRoutes: [] };

  const controlPlanLabels = new Set<string>();
  const tarpRoutes = new Map<string, AnalysisDiagnosisTarpRoute>();
  for (const candidate of diagnosis.candidates) {
    const controlPlan = controlPlanLabel(candidate);
    if (controlPlan) controlPlanLabels.add(controlPlan);
    const action = tarpLabel(candidate);
    if (!action || !candidate.tarp) continue;
    const key = `${candidate.tarp.ladder_id}:${candidate.tarp.tier}`;
    const route = tarpRoutes.get(key);
    if (route) {
      route.causeLabels.push(candidate.cause_label);
    } else {
      tarpRoutes.set(key, {
        key,
        label: action,
        causeLabels: [candidate.cause_label],
        tier: candidate.tarp.tier,
      });
    }
  }

  return {
    controlPlanLabels: [...controlPlanLabels],
    tarpRoutes: [...tarpRoutes.values()].sort(
      (left, right) => left.tier - right.tier || left.key.localeCompare(right.key),
    ),
  };
}
